import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/Shared";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BESOINS = [
  "Formation « IA & Prompt Engineering »",
  "Formation « Automatisation No-code »",
  "Formation « Hygiène alimentaire & Plan de Maîtrise Sanitaire »",
  "Audit d’établissement",
  "Livres & ressources",
  "Autre demande",
];

const inputCls =
  "w-full font-sans text-base text-marine bg-white border border-bordure rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-vert/60 focus:border-vert transition";

export default function Contact() {
  const [form, setForm] = useState({
    nom: "",
    structure: "",
    email: "",
    telephone: "",
    besoin: "",
    message: "",
    handicap: false,
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const validate = () => {
    const errs = {};
    if (form.nom.trim().length < 2) errs.nom = "Indiquez votre nom.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Indiquez un e-mail valide.";
    if (!form.besoin) errs.besoin = "Choisissez un besoin.";
    if (form.message.trim().length < 10) errs.message = "Décrivez votre besoin en quelques mots (10 caractères minimum).";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Votre demande est bien envoyée. Réponse sous 48 h ouvrées.");
      setForm({ nom: "", structure: "", email: "", telephone: "", besoin: "", message: "", handicap: false });
      setErrors({});
    } catch {
      toast.error("L’envoi a échoué. Réessayez ou écrivez à form.action1pro@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact & devis — réponse sous 48 h — Form Action Pro"
        description="Contactez Form Action Pro : devis de formation sous 48 h ouvrées. IA & prompt engineering, automatisation no-code, hygiène alimentaire & PMS, audit d'établissement. La Croix Valmer (Var)."
        path="/contact"
      />
      <PageHero
        kicker="Contact & devis"
        title="Parlons de votre besoin"
        subtitle="Décrivez votre projet en quelques lignes : devis personnalisé sous 48 h ouvrées, sans engagement."
      />

      <section className="bg-sable py-16">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1fr_340px] gap-10 items-start">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            noValidate
            className="bg-white border border-bordure rounded-lg p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="nom" className="block font-sans text-sm font-semibold text-marine mb-1.5">
                  Nom <span className="text-vert-dark">*</span>
                </label>
                <input id="nom" data-testid="contact-nom" type="text" value={form.nom} onChange={set("nom")} className={inputCls} autoComplete="name" />
                {errors.nom && <p data-testid="error-nom" className="font-sans text-xs text-red-600 mt-1">{errors.nom}</p>}
              </div>
              <div>
                <label htmlFor="structure" className="block font-sans text-sm font-semibold text-marine mb-1.5">
                  Structure
                </label>
                <input id="structure" data-testid="contact-structure" type="text" value={form.structure} onChange={set("structure")} className={inputCls} autoComplete="organization" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="email" className="block font-sans text-sm font-semibold text-marine mb-1.5">
                  E-mail <span className="text-vert-dark">*</span>
                </label>
                <input id="email" data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
                {errors.email && <p data-testid="error-email" className="font-sans text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="telephone" className="block font-sans text-sm font-semibold text-marine mb-1.5">
                  Téléphone
                </label>
                <input id="telephone" data-testid="contact-telephone" type="tel" value={form.telephone} onChange={set("telephone")} className={inputCls} autoComplete="tel" />
              </div>
            </div>
            <div>
              <label htmlFor="besoin" className="block font-sans text-sm font-semibold text-marine mb-1.5">
                Votre besoin <span className="text-vert-dark">*</span>
              </label>
              <select id="besoin" data-testid="contact-besoin" value={form.besoin} onChange={set("besoin")} className={inputCls}>
                <option value="">— Choisissez —</option>
                {BESOINS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              {errors.besoin && <p data-testid="error-besoin" className="font-sans text-xs text-red-600 mt-1">{errors.besoin}</p>}
            </div>
            <div>
              <label htmlFor="message" className="block font-sans text-sm font-semibold text-marine mb-1.5">
                Message <span className="text-vert-dark">*</span>
              </label>
              <textarea id="message" data-testid="contact-message" rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} />
              {errors.message && <p data-testid="error-message" className="font-sans text-xs text-red-600 mt-1">{errors.message}</p>}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="contact-handicap"
                checked={form.handicap}
                onChange={set("handicap")}
                className="mt-1 w-4 h-4 accent-[#7FB432]"
              />
              <span className="font-sans text-sm text-marine/80 leading-relaxed">
                Une personne concernée est en situation de handicap — je souhaite être recontacté(e)
                pour adapter les conditions de formation.
              </span>
            </label>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-vert hover:bg-vert-hover disabled:opacity-60 text-marine font-sans font-semibold text-base px-8 py-4 rounded transition-colors duration-200"
            >
              {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              {sending ? "Envoi en cours…" : "Envoyer ma demande"}
            </button>
            <p className="font-sans text-xs text-gris-texte leading-relaxed">
              Les informations transmises servent uniquement à répondre à votre demande. Voir la{" "}
              <a href="/confidentialite" data-testid="contact-lien-confidentialite" className="underline hover:text-vert-dark">
                politique de confidentialité
              </a>
              .
            </p>
          </form>

          <aside className="space-y-6">
            <div className="bg-marine rounded-lg p-8 text-white">
              <h2 className="font-serif text-2xl mb-6">Coordonnées</h2>
              <div className="space-y-4 font-sans text-sm">
                <p className="flex items-center gap-3">
                  <Mail size={17} className="text-vert shrink-0" />
                  <a href="mailto:form.action1pro@gmail.com" data-testid="contact-email-direct" className="hover:text-vert transition-colors break-all">
                    form.action1pro@gmail.com
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <Phone size={17} className="text-vert shrink-0" />
                  <a href="tel:+33769057673" data-testid="contact-tel-direct" className="hover:text-vert transition-colors">
                    07 69 05 76 73
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <MapPin size={17} className="text-vert shrink-0" />
                  La Croix Valmer (Var, 83)
                </p>
              </div>
            </div>
            <div className="bg-white border border-bordure rounded-lg p-8">
              <h3 className="font-serif text-xl text-marine mb-3">Délais</h3>
              <p className="font-sans text-sm text-gris-texte leading-relaxed">
                Réponse et devis sous 48 h ouvrées. Délai d’accès indicatif aux formations : 15
                jours ouvrés à compter de votre accord.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
